import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import StatCard from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Search, Plus, ChevronDown, Loader2, Users, UserX, AlertTriangle, Wallet, TrendingUp, ExternalLink, Download, Archive } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES, formatDate } from "@/lib/format";

const PAGE_SIZE = 20;
const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "pending_install", label: "Pending Install" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "suspended", label: "Suspended" },
  { value: "disabled", label: "Disabled" },
  { value: "terminated", label: "Terminated" },
  { value: "archived", label: "Archived" },
];
const emptyForm = { phone: "", plan_id: "", pppoe_username: "", static_ip: "", installation_address: "", custom_price_kes: "" };

export default function PppoeAccounts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [plans, setPlans] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("");

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch, locationFilter]);
  useEffect(() => { api.get("/admin/pppoe/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/pppoe/locations").then(({ data }) => setLocations(data)).catch(() => {}); }, []);

  const loadDashboard = async () => {
    try {
      const { data } = await api.get("/admin/pppoe/dashboard");
      setDashboard(data);
    } catch { /* stat row is a nice-to-have, don't block the page on it */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pppoe/accounts", {
        params: { status, q: debouncedSearch || undefined, location_id: locationFilter || undefined, page, page_size: PAGE_SIZE },
      });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    loadDashboard();
  };
  useEffect(() => { load(); }, [status, debouncedSearch, locationFilter, page]);

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detail[id]) {
      try {
        const { data } = await api.get(`/admin/pppoe/accounts/${id}`);
        setDetail((cur) => ({ ...cur, [id]: data }));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    }
  };

  const createAccount = async () => {
    if (!form.phone || !form.plan_id || !form.pppoe_username) {
      toast.error("Phone, plan, and username are required");
      return;
    }
    try {
      await api.post("/admin/pppoe/accounts", {
        ...form,
        custom_price_kes: form.custom_price_kes ? Number(form.custom_price_kes) : undefined,
      });
      toast.success("Account created — pending install");
      setAddOpen(false); setForm(emptyForm);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const runAction = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/admin/pppoe/accounts/${id}/${action}`);
      toast.success(
        action === "provision" ? "Provisioned — credentials pushed" :
        action === "suspend" ? "Suspended" :
        action === "archive" ? "Archived" : "Reactivated"
      );
      await load();
      if (detail[id]) {
        const { data } = await api.get(`/admin/pppoe/accounts/${id}`);
        setDetail((cur) => ({ ...cur, [id]: data }));
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusyId(null); }
  };

  const archiveAccount = (id) => {
    if (!window.confirm("Archive this service? It stops providing service and its RADIUS credentials are removed.")) return;
    runAction(id, "archive");
  };

  const recordPayment = async (accountId, invoiceId) => {
    if (!confirm("Record this invoice as paid (cash/bank, outside M-Pesa)?")) return;
    try {
      await api.post(`/admin/pppoe/invoices/${invoiceId}/record-payment`, {});
      toast.success("Payment recorded");
      await load();
      const { data } = await api.get(`/admin/pppoe/accounts/${accountId}`);
      setDetail((cur) => ({ ...cur, [accountId]: data }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const statusBadge = (s) => {
    const styles = {
      active: "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10",
      pending_install: "text-nhp-info border-nhp-info/40 bg-nhp-info/10",
      paused: "text-nhp-muted border-nhp-border",
      suspended: "text-nhp-warning border-nhp-warning/40 bg-nhp-warning/10",
      disabled: "text-nhp-critical border-nhp-critical/40 bg-nhp-critical/10",
      terminated: "text-nhp-critical border-nhp-critical/40 bg-nhp-critical/10",
      archived: "text-nhp-muted border-nhp-border",
    };
    return (
      <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${styles[s] || "text-nhp-muted border-nhp-border"}`}>
        {s.replace("_", " ")}
      </span>
    );
  };

  return (
    <div>
      <PageHeader
        testId="pppoe-accounts-header"
        title="PPPoE Accounts"
        subtitle="Wired customer accounts, billing, and provisioning"
        actions={
          <>
          <Link to="/admin/pppoe-accounts/import">
            <Button variant="outline" className="h-10 rounded-none font-mono text-xs">
              <Download className="h-4 w-4 mr-2" /> IMPORT LEGACY
            </Button>
          </Link>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
                <Plus className="h-4 w-4 mr-1" /> NEW ACCOUNT
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">New PPPoE account</DialogTitle>
                <DialogDescription className="text-nhp-muted">
                  Registers the account as pending install. Provision it once the technician confirms the wiring is done.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Customer phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0712345678" className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Plan</Label>
                  <select
                    value={form.plan_id}
                    onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                    className="mt-1 w-full h-10 bg-nhp-bg border border-nhp-border px-3 text-sm"
                  >
                    <option value="">Select a plan…</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} — {KES(p.price_kes)}/{p.billing_period_days}d</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">PPPoE username</Label>
                  <Input value={form.pppoe_username} onChange={(e) => setForm({ ...form, pppoe_username: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Static IP (optional)</Label>
                  <Input value={form.static_ip} onChange={(e) => setForm({ ...form, static_ip: e.target.value })} placeholder="Leave blank for dynamic" className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Installation address</Label>
                  <Input value={form.installation_address} onChange={(e) => setForm({ ...form, installation_address: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Custom price (optional — overrides plan price)</Label>
                  <Input type="number" value={form.custom_price_kes} onChange={(e) => setForm({ ...form, custom_price_kes: e.target.value })} placeholder="Leave blank to use the plan's price" className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAddOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={createAccount} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">CREATE</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard testId="pppoe-stat-active" label="Active" value={dashboard.active_count} icon={Users} accent="text-nhp-primary" />
            <StatCard testId="pppoe-stat-suspended" label="Suspended" value={dashboard.suspended_count} icon={UserX} accent="text-nhp-warning" />
            <StatCard testId="pppoe-stat-overdue" label="Overdue" value={dashboard.overdue_count} icon={AlertTriangle} accent="text-nhp-critical" />
            <StatCard testId="pppoe-stat-mrr" label="MRR" value={KES(dashboard.mrr_kes)} icon={Wallet} accent="text-nhp-primary" />
            <StatCard testId="pppoe-stat-revenue" label="Revenue (month)" value={KES(dashboard.revenue_month_kes)} icon={TrendingUp} accent="text-nhp-primary" />
          </div>
        )}

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-sm w-full">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-nhp-muted" />
            <Input
              placeholder="Search by username or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-nhp-surface border-nhp-border text-nhp-text rounded-none h-10 font-mono"
            />
          </div>
          {locations.length > 0 && (
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="h-10 bg-nhp-surface border border-nhp-border px-3 text-sm"
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.country} / {l.city}{l.suburb ? ` / ${l.suburb}` : ""}</option>
              ))}
            </select>
          )}
          <Tabs value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <TabsList className="bg-nhp-surface border border-nhp-border rounded-none h-10 overflow-x-auto max-w-full">
              {STATUS_TABS.map((t) => (
                <TabsTrigger key={t.value} value={t.value} className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary whitespace-nowrap">
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-2">Username</div>
            <div className="col-span-1">Account No.</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Plan</div>
            <div className="col-span-1">IP</div>
            <div className="col-span-1">Paid through</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {items.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">No PPPoE accounts.</div>}
            {items.map((a) => (
              <div key={a.id}>
                <button
                  type="button"
                  onClick={() => toggleExpand(a.id)}
                  className="w-full flex flex-col gap-2 md:grid md:grid-cols-12 px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg text-left"
                >
                  <div className="flex items-center justify-between md:contents">
                    <div className="font-mono md:col-span-2 flex items-center gap-2 min-w-0">
                      <ChevronDown className={`h-3.5 w-3.5 text-nhp-muted transition-transform shrink-0 ${expanded === a.id ? "rotate-180" : ""}`} />
                      <span className="truncate">{a.pppoe_username}</span>
                      {a.source === "legacy_import" && (
                        <span className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-nhp-info/40 text-nhp-info bg-nhp-info/10 shrink-0">UCRM</span>
                      )}
                    </div>
                    <div className="md:col-span-1">{statusBadge(a.status)}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-nhp-muted md:contents">
                    <div className="font-mono text-xs md:col-span-1">{a.account_number || "—"}</div>
                    <div className="font-mono md:col-span-2">{a.customer_phone}</div>
                    <div className="md:col-span-2 text-nhp-text">{a.plan_name}</div>
                    <div className="font-mono text-xs md:col-span-1">{a.ip_address || "—"}</div>
                    <div className="font-mono text-xs md:col-span-1">{a.current_period_end ? formatDate(a.current_period_end) : "—"}</div>
                  </div>
                  <div className="md:col-span-2 md:text-right flex flex-wrap gap-2 md:justify-end items-center" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/admin/pppoe-accounts/${a.id}`} className="h-8 w-8 grid place-items-center border border-nhp-border text-nhp-muted hover:text-nhp-text" title="View profile">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                    {a.source === "legacy_import" ? (
                      <span className="text-xs text-nhp-muted italic">managed in UCRM</span>
                    ) : (
                    <>
                    {a.status === "pending_install" && (
                      <Button size="sm" disabled={busyId === a.id} onClick={() => runAction(a.id, "provision")} className="h-8 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
                        {busyId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "PROVISION"}
                      </Button>
                    )}
                    {a.status === "active" && (
                      <Button size="sm" variant="outline" disabled={busyId === a.id} onClick={() => runAction(a.id, "suspend")} className="h-8 rounded-none text-xs">
                        {busyId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "SUSPEND"}
                      </Button>
                    )}
                    {["suspended", "paused", "disabled"].includes(a.status) && (
                      <Button size="sm" disabled={busyId === a.id} onClick={() => runAction(a.id, "reactivate")} className="h-8 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">
                        {busyId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "REACTIVATE"}
                      </Button>
                    )}
                    {a.status !== "archived" && (
                      <button
                        type="button" title="Archive" disabled={busyId === a.id} onClick={() => archiveAccount(a.id)}
                        className="h-8 w-8 grid place-items-center border border-nhp-border text-nhp-muted hover:text-nhp-text"
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                    )}
                    </>
                    )}
                  </div>
                </button>

                {expanded === a.id && (
                  <div className="px-4 md:px-6 pb-4 bg-nhp-bg">
                    {!detail[a.id] ? (
                      <div className="py-4 text-sm text-nhp-muted">Loading invoices...</div>
                    ) : detail[a.id].invoices.length === 0 ? (
                      <div className="py-4 text-sm text-nhp-muted">No invoices yet.</div>
                    ) : (
                      <div className="divide-y divide-nhp-border border border-nhp-border bg-nhp-surface mt-2">
                        {detail[a.id].invoices.map((inv) => (
                          <div key={inv.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                            <div className="font-mono text-xs text-nhp-muted">
                              {formatDate(inv.period_start)} → {formatDate(inv.period_end)}
                            </div>
                            <div className="font-mono font-bold text-nhp-primary">{KES(inv.amount_kes)}</div>
                            <div className="flex items-center gap-2">
                              <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${inv.status === "paid" ? "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10" : "text-nhp-muted border-nhp-border"}`}>
                                {inv.status}
                              </span>
                              {inv.status === "unpaid" && a.source !== "legacy_import" && (
                                <Button size="sm" variant="outline" onClick={() => recordPayment(a.id, inv.id)} className="h-7 rounded-none text-xs">
                                  RECORD PAYMENT
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="accounts" onChange={setPage} testId="pppoe-accounts" />
        </div>
      </div>
    </div>
  );
}
